import { useMutation } from "@apollo/client";
import Block from "@mui/icons-material/Block";
import Handshake from "@mui/icons-material/Handshake";
import { Alert, Box, Button, Chip, Divider, Drawer, Link, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  FeatureFlagTogglePanel,
  FlagState,
  PendingValue,
  collectDependencyKeysToActivate,
  collectDependencyKeysToDeactivate,
  countPendingChanges,
} from "components/FeatureFlags/FeatureFlagTogglePanel";
import {
  modulesEnabledLabels,
  TenantFeatureFlagChips,
} from "components/FeatureFlags/TenantFeatureFlagChips";
import { TENANT_FEATURE_FLAG_FILTER_COLUMNS } from "components/FeatureFlags/tenantFlagFilterColumns";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  FeatureFlagKey,
  getFeatureFlagMeta,
  KNOWN_FEATURE_FLAG_KEYS,
} from "encamp-shared/src/featureFlags";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { gql } from "../../../generated-graphql";
import {
  TenantsFeatureFlagsQuery,
  TenantsFeatureFlagsQueryVariables,
} from "../../../generated-graphql/graphql";
import { FeatureFlagsTab } from "../Implementation/OrganizationDetails/FeatureFlags";

// Exported because the cross-tenant FlagsTab tests want to share the same
// row → tenantConfigs shape when stubbing the query response.
export const TENANTS_FEATURE_FLAGS_QUERY = gql(`
  query TenantsFeatureFlags($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        isDemo
        isPartner
        isDisabled
        tenantConfigs {
          id
          tenantId
          key
          value
        }
      }
      count
    }
  }
`);

const BULK_SET_FLAGS_MUTATION = gql(`
  mutation BulkSetTenantFeatureFlags($inputs: [SetTenantFeatureFlagInput!]!) {
    setTenantFeatureFlags(inputs: $inputs) {
      id
      tenantConfigs {
        id
        tenantId
        key
        value
      }
    }
  }
`);

type TenantConfigRow = {
  id: string;
  tenantId: string;
  key: string;
  value: boolean;
};

type TenantRow = {
  id: string;
  name: string;
  isDemo: boolean;
  isPartner: boolean;
  isDisabled: boolean;
  tenantConfigs: TenantConfigRow[];
};

/**
 * Tenant-centric (one row per organization) view of feature flags. This is
 * the original Staff Feature Flags page — wrapped here as the
 * `?tab=organizations` panel of the new tabbed layout in `index.tsx`.
 * Behavior is unchanged from before the tab refactor.
 */
export function OrganizationsTab() {
  const alerts = useAlerts();
  const [selectedRows, setSelectedRows] = useState<TenantRow[]>([]);
  const [pendingChanges, setPendingChanges] = useState<
    Partial<Record<FeatureFlagKey, PendingValue>>
  >({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Row-level drill-in: clicking a row (not the checkbox) opens a
  // right-side drawer scoped to that single tenant. `checkboxSelection`
  // in MUI DataGrid already suppresses `onRowClick` for clicks inside
  // the checkbox cell, so single-tenant drill-in and bulk-select don't
  // conflict.
  const [viewingTenant, setViewingTenant] = useState<TenantRow | null>(null);

  const [bulkSet, { loading: applyingBulk }] = useMutation(BULK_SET_FLAGS_MUTATION);

  const openDrawer = () => {
    setPendingChanges({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setPendingChanges({});
  };

  const currentStates = useMemo<Partial<Record<FeatureFlagKey, FlagState>>>(
    () => computeCurrentStatesAcrossTenants(selectedRows),
    [selectedRows]
  );

  // One row per *flag* the user staged (with its current cross-tenant
  // state and the value they picked). Keeps the Review-changes dialog
  // honest about "I touched 3 flags", regardless of how many tenant
  // writes that fans out to. Ordered by KNOWN_FEATURE_FLAG_KEYS so the
  // dialog list matches the panel's visual order.
  const flagChanges = useMemo(() => {
    const rows: { key: FeatureFlagKey; current: FlagState; next: boolean }[] = [];
    for (const key of KNOWN_FEATURE_FLAG_KEYS) {
      const next = pendingChanges[key];
      if (next === undefined) continue;
      rows.push({ key, current: currentStates[key] ?? false, next });
    }
    return rows;
  }, [pendingChanges, currentStates]);

  // Flatten pending changes across selected tenants, skipping no-ops (tenant
  // already at the desired value via TenantConfig or defaultValue).
  const pendingInputs = useMemo(() => {
    const inputs: { tenantId: string; key: FeatureFlagKey; value: boolean }[] = [];
    for (const tenant of selectedRows) {
      for (const entry of Object.entries(pendingChanges) as [FeatureFlagKey, PendingValue][]) {
        const [key, next] = entry;
        if (next === undefined) continue;
        const existing = tenant.tenantConfigs.find((c) => c.key === key);
        const existingValue = existing?.value ?? getFeatureFlagMeta(key).defaultValue;
        if (existingValue === next) continue;
        inputs.push({ tenantId: tenant.id, key, value: next });
      }
    }
    return inputs;
  }, [selectedRows, pendingChanges]);

  const touchesMeasurements = pendingInputs.some((i) => i.key === FeatureFlagKey.Measurements);

  const applyChanges = useCallback(async () => {
    try {
      await bulkSet({ variables: { inputs: pendingInputs } });
      alerts.success(
        `Updated ${pendingInputs.length} flag ${
          pendingInputs.length === 1 ? "setting" : "settings"
        } across ${selectedRows.length} ${selectedRows.length === 1 ? "tenant" : "tenants"}.`
      );
      setConfirmOpen(false);
      closeDrawer();
    } catch (err) {
      alerts.error("Failed to apply feature flag changes", err);
    }
  }, [alerts, bulkSet, pendingInputs, selectedRows.length]);

  const columns: OmnisearchGridColDef<TenantRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Organization",
        flex: 1,
      },
      {
        field: "modules",
        headerName: "Modules enabled",
        flex: 1.5,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => modulesEnabledLabels(row.tenantConfigs).join(", "),
        renderCell: ({ row }) => <TenantFeatureFlagChips tenantConfigs={row.tenantConfigs} />,
      },
      // Partner / Disabled / Demo mirror the equivalent columns on the
      // canonical Staff Organizations page (`OrganizationTable`) so the
      // two grids read the same. The legacy table uses a raw 🛑 emoji
      // for Disabled; we use the MUI `Block` icon here so both
      // icon-only cells render with the same component family and
      // visual weight inside the same column band.
      {
        field: "isPartner",
        headerName: "Partner",
        flex: 0.25,
        align: "center",
        headerAlign: "center",
        filterKeyType: "boolean",
        renderCell: ({ value }) => (value ? <Handshake /> : null),
      },
      {
        field: "isDisabled",
        headerName: "Disabled",
        flex: 0.25,
        align: "center",
        headerAlign: "center",
        filterKeyType: "boolean",
        renderCell: ({ value }) => (value ? <Block color="error" /> : null),
      },
      {
        field: "isDemo",
        headerName: "Demo",
        flex: 0.4,
        filterKeyType: "boolean",
        renderCell: ({ value }) => (value ? "Yes" : ""),
      },
    ],
    []
  );

  // The Modules / Flags Enabled filter chips are shared with the Staff
  // Organizations page — see `TENANT_FEATURE_FLAG_FILTER_COLUMNS` for
  // the wiring (both alias to the server-side `hasFlag` omnisearch key).
  const additionalFilterColumns = TENANT_FEATURE_FLAG_FILTER_COLUMNS;

  const bulkSetButton = (
    <CommandButton
      key="bulk-set-flags"
      variant="contained"
      disabled={selectedRows.length === 0}
      onClick={openDrawer}
    >
      Bulk Set Flags
      {selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
    </CommandButton>
  );

  // Hoisted once so the sticky footer's visibility guard, bold
  // count, singular/plural switch, and Apply-button disabled state
  // all read from the same value instead of recomputing it four
  // times per render. Plain `const` is enough — primitive over a
  // small object, no downstream hook needs referential stability.
  const pendingCount = countPendingChanges(pendingChanges);

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 2 }}>
        See flags set per organization and bulk set them. (You can also do this individually on the
        staff{" "}
        <Link component={RouterLink} to="/staff/implementation" underline="hover">
          Organizations
        </Link>{" "}
        page.)
      </Typography>
      <OmnisearchDataGrid<TenantsFeatureFlagsQuery, TenantsFeatureFlagsQueryVariables, TenantRow>
        persistenceKey="staff-feature-flags-by-tenant"
        isURLDriven
        checkboxSelection
        rowHeight="auto"
        dataQuery={TENANTS_FEATURE_FLAGS_QUERY}
        columns={columns}
        getCount={(data) => data.tenants.count}
        getItems={(data) => data.tenants.items}
        onSelectedRowsChanged={setSelectedRows}
        onRowClick={(params) => setViewingTenant(params.row)}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        // Clicking a row body drills into the single-tenant modal.
        // Only the checkbox cell toggles bulk selection — so the two
        // interactions stay independent.
        disableRowSelectionOnClick
        gridSx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        commandButtons={[bulkSetButton]}
        additionalFilterColumns={additionalFilterColumns}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        // Lock the drawer paper to a flex column with no scrolling of
        // its own. The middle <Box> below owns scrolling so the footer
        // can stay pinned to the bottom of the viewport regardless of
        // how many flags expand inside the toggle panel.
        slotProps={{
          paper: {
            sx: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            width: { xs: "100vw", md: 560 },
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Typography variant="h5">
              Set flags for {selectedRows.length} {selectedRows.length === 1 ? "tenant" : "tenants"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Only flags you change will be updated. Flags you don&apos;t touch keep each selected
              tenant&apos;s existing value.
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              // Reserve scrollbar gutter so the toggle column doesn't
              // reflow horizontally when expanding/collapsing modules
              // toggles vertical overflow on/off (same reason the
              // legacy drawer paper had this set).
              scrollbarGutter: "stable",
              px: 3,
              pb: 2,
            }}
          >
            <FeatureFlagTogglePanel
              mode="multi"
              currentStates={currentStates}
              pendingChanges={pendingChanges}
              onChange={(key, next) =>
                setPendingChanges((prev) => {
                  const updated: Partial<Record<FeatureFlagKey, PendingValue>> = { ...prev };
                  if (next === undefined) {
                    delete updated[key];
                    return updated;
                  }
                  updated[key] = next;
                  // Cascade across pending + current state. A flag is
                  // treated as "on" for this check if its pending value
                  // is true or (no pending change) every selected tenant
                  // already has it on — matching the hint the user sees
                  // in the panel. For "off", the inverse.
                  const isOn = (k: FeatureFlagKey): boolean => {
                    if (updated[k] !== undefined) return updated[k] === true;
                    return currentStates[k] === true;
                  };
                  if (next === true) {
                    // Turn on parents + `requires` deps so the newly
                    // enabled flag functions.
                    for (const depKey of collectDependencyKeysToActivate(key, isOn)) {
                      updated[depKey] = true;
                    }
                  } else {
                    // Turn off children + anything that requires this
                    // flag, so we never leave dependents inactive.
                    for (const depKey of collectDependencyKeysToDeactivate(key, isOn)) {
                      updated[depKey] = false;
                    }
                  }
                  return updated;
                })
              }
            />
          </Box>
          <Divider />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 3, py: 2, bgcolor: "background.paper" }}
          >
            {/* Pending count sits on the left of the sticky footer
                so it never pops into existence and shoves the
                FlagsPanel rows down — the old top-of-body Alert
                used to do exactly that. Empty `Box` keeps the
                buttons right-aligned when the count is zero. */}
            <Box flex={1}>
              {pendingCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{pendingCount}</strong> {pendingCount === 1 ? "flag" : "flags"} pending
                  change
                </Typography>
              )}
            </Box>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button
              variant="contained"
              disabled={pendingCount === 0 || pendingInputs.length === 0}
              onClick={() => setConfirmOpen(true)}
            >
              Review changes
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={confirmOpen}
        header="Apply feature flag changes?"
        confirmText={`Apply (${flagChanges.length})`}
        loading={applyingBulk}
        onClose={() => setConfirmOpen(false)}
        onConfirm={applyChanges}
      >
        <Stack spacing={2}>
          <Typography variant="body2">
            This will make <strong>{flagChanges.length}</strong> flag{" "}
            {flagChanges.length === 1 ? "change" : "changes"} across{" "}
            <strong>{selectedRows.length}</strong>{" "}
            {selectedRows.length === 1 ? "tenant" : "tenants"}.
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 3,
              maxHeight: 320,
              overflowY: "auto",
              // Bullet markers + a generous line-height read better
              // than a flat divider stack for "here's the list of
              // things I'm about to do".
              "& > li": { mb: 0.75 },
              "& > li:last-of-type": { mb: 0 },
            }}
          >
            {flagChanges.map(({ key, current, next }) => (
              <Stack
                component="li"
                key={key}
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {getFeatureFlagMeta(key).label}:
                </Typography>
                <FlagStateChip state={current} />
                <Typography variant="body2" component="span" aria-hidden>
                  →
                </Typography>
                <FlagStateChip state={next} />
              </Stack>
            ))}
          </Box>
          {touchesMeasurements && (
            <Alert severity="info">
              Toggling the Measurements flag triggers chemical aggregation for each affected tenant
              across all active reporting years.
            </Alert>
          )}
        </Stack>
      </ConfirmDialog>

      <Drawer
        anchor="right"
        open={viewingTenant !== null}
        onClose={() => setViewingTenant(null)}
        slotProps={{ paper: { sx: { scrollbarGutter: "stable" } } }}
      >
        <Box sx={{ width: { xs: "100vw", md: 560 }, p: 3, bgcolor: "background.paper" }}>
          {viewingTenant && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5">{viewingTenant.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Feature flags
                </Typography>
              </Box>
              <FeatureFlagsTab tenantId={viewingTenant.id} />
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
}

/**
 * Compact, color-coded `<Chip>` for representing a `FlagState` in
 * the Review-changes confirmation list. Three palettes:
 *   - ON   → primary/blue (matches the "on" state across the app)
 *   - OFF  → neutral grey (no semantic weight; just "not enabled")
 *   - MIXED → warning amber (echoes the indeterminate switch's
 *             "needs your attention / not settled" semantic)
 */
function FlagStateChip({ state }: { state: FlagState }) {
  if (state === true) {
    return (
      <Chip
        label="ON"
        size="small"
        sx={(theme) => ({
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
        })}
      />
    );
  }
  if (state === false) {
    return (
      <Chip
        label="OFF"
        size="small"
        sx={(theme) => ({
          bgcolor: theme.palette.grey[300],
          color: theme.palette.text.primary,
          fontWeight: 600,
        })}
      />
    );
  }
  return (
    <Chip
      label="MIXED"
      size="small"
      sx={(theme) => ({
        bgcolor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
        fontWeight: 600,
      })}
    />
  );
}

/**
 * Given N selected tenants, derive the per-flag tri-state:
 *   - `true`    if every tenant currently has the flag enabled
 *   - `false`   if every tenant currently has the flag disabled
 *   - `"mixed"` otherwise
 * Tenants missing a TenantConfig row fall back to the flag's
 * `defaultValue` from `getFeatureFlagMeta`.
 */
export function computeCurrentStatesAcrossTenants(
  tenants: Pick<TenantRow, "tenantConfigs">[]
): Partial<Record<FeatureFlagKey, FlagState>> {
  const states: Partial<Record<FeatureFlagKey, FlagState>> = {};
  if (tenants.length === 0) return states;

  for (const key of KNOWN_FEATURE_FLAG_KEYS) {
    let sawTrue = false;
    let sawFalse = false;
    for (const tenant of tenants) {
      const existing = tenant.tenantConfigs.find((c) => c.key === key);
      const value = existing?.value ?? getFeatureFlagMeta(key).defaultValue;
      if (value) sawTrue = true;
      else sawFalse = true;
      if (sawTrue && sawFalse) break;
    }
    if (sawTrue && sawFalse) states[key] = "mixed";
    else if (sawTrue) states[key] = true;
    else states[key] = false;
  }
  return states;
}
